import * as React from "react"
import { FC, useEffect, useState } from "react"
import { AdminUserProps, TableColumn, User } from "../../../../types"
import adminUserColumns from "./columns/adminUserColumns"
import { connect } from "react-redux"
import {
  fetchAllUsers,
  fetchLastUpdate,
  refreshLabelCamp,
  refreshReports,
  refreshSummaries,
  selectUser
} from "../../../../actions/adminActions"
import { RootState } from "../../../../store"
import { assistant, crawlers } from "../../../../apis"
import { ADMIN_PERMISSIONS, adminTableLimit, USER_MEMBER_TYPE, USER_ROLE, userTypeOptions } from "../../../../constants"
import { KButton, KInput, KSpan, KTitleSpan, KTooltip, KDropdown } from "kahuna-base-react-components"
import TableNew from "../../../ui/table/TableNew"
import { KSelectOption } from "kahuna-base-react-components/dist/components/KDropdown/KDropdown"
import ReloadModal from "./modals/ReloadModal"
// @ts-ignore
import PlusIcon from "../../../../assets/release-registry-icons/plus.svg"
import AdminUserCreateModal from "./modals/AdminUserCreateModal"
import { AdminUserAssetTabOptions } from "../AdminUserAsset"
import AdminNewLabelModal from "./modals/AdminNewLabelModal"
import { checkAdminPermission } from "../../../../utility"
import AdminUserLog from "./AdminUserLog"
import AdminTakedownLog from "./takedown-logs/AdminTakedownLog"
import AdminNotificationWatch from "./AdminNotificationWatch"
import AdminAnnouncementWatch from "./AdminAnnouncementWatch"
import { toast } from "react-toastify"
import AdminDrmLog from "./takedown-logs/AdminDrmLog"
import AdminClaimLog from "./claim-logs/AdminClaimLog"
import AdminLabelRequest from "./label-requests/AdminLabelRequest"
import AdminExclusivityLog from "./takedown-logs/AdminExclusivityLog"
import AdminUserFilter from "./filters/AdminUserFilter"
import AdminCoManagerLog from "./co-manager-logs/AdminCoManagerLog"
import { DateRangeType } from "kahuna-base-react-components/dist/components/KSelectRange/KSelectRange"

enum PageOptions {
  USERS,
  USER_LOGS,
  TAKEDOWN_LOGS,
  CLAIM_LOGS,
  DRM_LOGS,
  EXCLUSIVITY_LOGS,
  NOTIFICATIONS,
  ANNOUNCEMENTS,
  LABEL_REQUESTS,
  CO_MANAGER_LOGS
}

export type AdminUserFilterType = {
  dateValues: DateRangeType
  distributionType?: KSelectOption
}

const initialUserFilter: AdminUserFilterType = {
  dateValues: [null, null],
  distributionType: undefined
}

const AdminUser: FC<AdminUserProps> = (props) => {
  const [loading, setLoading] = useState(true)
  const [timeoutId, setTimeoutId] = useState(undefined)
  const [inputTimeoutId, setInputTimeoutId] = useState(undefined)
  const [openSidebar, setOpenSidebar] = useState<boolean>(true)
  const [selectedPage, setSelectedPage] = useState<number>(PageOptions.USERS)
  const [filterOpen, setFilterOpen] = useState(false)

  const [pageNumber, setPageNumber] = useState<number>(Math.max(1, Number(props.params.get("page") || 1)))
  const [searchValue, setSearchValue] = useState<string>("")
  const searchOptions: KSelectOption[] = [
    { label: "User Name", value: 0, value2: "username" },
    { label: "First Name", value: 1, value2: "first_name" },
    { label: "Last Name", value: 2, value2: "last_name" }
  ]
  const [selectedSearchOption, setSelectedSearchOption] = useState<KSelectOption>(searchOptions[0])
  const [additionalFilters, setAdditionalFilters] = useState<{ [key: string]: string | undefined }>({})
  // Claims artists have filed that nobody has closed. Local rather than in the store, unlike the
  // three counts beside it in the strip: nothing else on the page changes it, and the claims screen
  // reads its own list anyway.
  const [openClaimCount, setOpenClaimCount] = useState(0)
  // Labels artists asked for from the co-manager that nobody has created or rejected yet. Working them
  // creates labels at Label Camp, so only an admin who can already do that from the user modal sees them.
  const [openLabelRequestCount, setOpenLabelRequestCount] = useState(0)
  const canWorkLabelRequests = checkAdminPermission(
    ADMIN_PERMISSIONS.USER_ASSET_WRITE,
    props.loggedInUser?.adminPermissions
  )
  const [hoveredRowId, setHoveredRowId] = useState<number | undefined>(undefined)
  const [newLabelModal, setNewLabelModal] = useState(false)

  const [reloadModalOpen, setReloadModalOpen] = useState<boolean>(false)
  const [addEditUserModal, setAddEditUserModalOpen] = useState<boolean>(false)
  const [users, setUsers] = useState<User[]>([])
  const [hideMembers, setHideMembers] = useState<boolean>(false)
  const [userRolesOpen, setUserRolesOpen] = useState(false)
  const [advanceFilter, setAdvanceFilter] = useState<AdminUserFilterType>(initialUserFilter)

  const adminUserCols: TableColumn[] = adminUserColumns(
    checkAdminPermission(ADMIN_PERMISSIONS.USER_ASSET_WRITE, props.loggedInUser?.adminPermissions),
    props.setModalOption,
    props.selectUser,
    setAddEditUserModalOpen
  )
  const [columns, setColumns] = useState<TableColumn[]>(adminUserCols)

  const roleParamToState = () => {
    const roleParam = props.params.get("role")
    if (!roleParam) return []

    const roles = roleParam.split(",")

    const result: number[] = []

    roles.forEach((role) => {
      const opt = userTypeOptions.find((option) => option?.value2 === role)
      if (opt) result.push(opt.value)
    })

    return result
  }

  const [selectedUserTypes, setSelectedUserTypes] = useState<number[]>(() => roleParamToState())

  useEffect(() => {
    // Counted here rather than on the claims screen, because the point of the strip is that the
    // work is visible before anybody opens it.
    crawlers("/claim-logs/open-count/", "GET")
      .then((response) => setOpenClaimCount(response.data.count || 0))
      .catch(() => setOpenClaimCount(0))
    fetchOpenLabelRequestCount()
  }, [selectedPage])

  const fetchOpenLabelRequestCount = () => {
    if (!canWorkLabelRequests) return
    assistant("/label-requests/open-count/", "GET", null, {}, false)
      .then((response) => setOpenLabelRequestCount(response.data.count || 0))
      .catch(() => setOpenLabelRequestCount(0))
  }

  useEffect(() => {
    if (
      props.params.has("log_id") ||
      props.params.has("log_upc") ||
      props.params.has("log_user_id") ||
      props.params.has("audit_user_id")
    ) {
      setSelectedPage(PageOptions.USER_LOGS)
    }
  }, [props.params])

  useEffect(() => {
    const results = props.paginatedUsers.results
    const processedUsers: User[] = []
    results.forEach((user) => {
      processedUsers.push(user)
      if (hideMembers) return

      user.userMembers?.forEach((member) => {
        processedUsers.push({ ...member, isUserMember: true })
      })
    })
    setUsers(processedUsers)
  }, [props.paginatedUsers, hideMembers])

  const stateToRoleParam = (values: number[]) => {
    if (values.length === 0) return ""

    const parts = values
      .map((value) => {
        const opt = userTypeOptions.find((option) => option.value === value)
        return opt ? opt.value2 : null
      })
      .filter(Boolean) as string[]
    return parts.join(",")
  }

  useEffect(() => {
    props.setParams((prev) => {
      const obj = Object.fromEntries(prev) as Record<string, string>

      delete obj.user_id

      obj.page = String(pageNumber)

      const roleStr = stateToRoleParam(selectedUserTypes)
      if (roleStr) obj.role = roleStr
      else delete obj.role

      return obj
    })
  }, [pageNumber, selectedUserTypes])

  const onClickPage = async (
    pageNumber: number,
    filters: { [key: string]: string | number | boolean } | undefined,
    sortField?: string,
    sortDirection?: "asc" | "desc"
  ) => {
    setLoading(true)
    await props.fetchAllUsers(adminTableLimit, (pageNumber - 1) * adminTableLimit, filters, sortField, sortDirection)
    setLoading(false)
    setPageNumber(pageNumber)
  }

  const onSortOrFilterChange = async (
    filters: { [key: string]: string | number | boolean },
    sortField: string,
    sortDirection?: "asc" | "desc",
    debounce?: boolean
  ) => {
    // runs in first render (mount) without applying any filter or sort param
    if (timeoutId) {
      clearTimeout(timeoutId)
    }
    setTimeoutId(
      //@ts-ignore
      setTimeout(
        async () => {
          const page = pageNumber
          const offset = (page - 1) * adminTableLimit
          setLoading(true)
          await props.fetchAllUsers(adminTableLimit, offset, filters, sortField, sortDirection)
          setLoading(false)
        },
        debounce ? 500 : 0
      )
    )
  }

  useEffect(() => {
    clearTimeout(inputTimeoutId)
    setInputTimeoutId(
      // @ts-ignore
      setTimeout(async () => {
        if (selectedSearchOption?.value2) {
          const currentFilters = { ...additionalFilters }
          setAdditionalFilters({
            username: selectedSearchOption?.value2 === "username" ? searchValue : "",
            first_name: selectedSearchOption?.value2 === "first_name" ? searchValue : "",
            last_name: selectedSearchOption?.value2 === "last_name" ? searchValue : "",
            role__id: selectedUserTypes.length > 0 ? selectedUserTypes.join(",") : undefined,
            has_update: currentFilters.has_update,
            contract_verified: currentFilters.contract_verified
          })
        }
      }, 500)
    )
  }, [selectedSearchOption, searchValue, selectedUserTypes])

  const onClickAdd = () => {
    props.selectUser(new User())
    setAddEditUserModalOpen(true)
  }

  const onSelectRow = (index: number) => {
    if (index === -1) return // onClickPagination triggers onSelectRow with index -1, so I cancelled it
    const clickedUser = users[index]
    props.selectUser(clickedUser)
    props.setTab(AdminUserAssetTabOptions.ASSET)

    props.setParams({ user_id: String(clickedUser.id) })
  }

  const onApplyAdvanceFilter = () => {
    const processedFilters = { date_joined: "", distribution_type: "" }
    if (advanceFilter.dateValues && advanceFilter.dateValues[0] && advanceFilter.dateValues[1]) {
      const start = advanceFilter.dateValues[0]
      start.setHours(9)
      const end = advanceFilter.dateValues[1]
      end.setHours(9)
      end.setMonth(end.getMonth() + 1)
      processedFilters.date_joined = `${start.toISOString().slice(0, 10)},${end.toISOString().slice(0, 10)}`
    }
    if (advanceFilter.distributionType) {
      processedFilters.distribution_type = advanceFilter.distributionType.value2 || ""
    }
    setAdditionalFilters({ ...additionalFilters, ...processedFilters })
  }

  const topRightElements = (
    <div className="flex flex-row items-center gap-3">
      <div className="cursor-pointer" onClick={() => setFilterOpen(!filterOpen)}>
        <KTooltip
          position="bottom"
          children={<img width={20} src={"/catalog_icons/filter-original.svg"} />}
          content={<KSpan text={filterOpen ? "Close Filters" : "Open Filters"} />}
        />
      </div>
      <div className="cursor-pointer" onClick={() => setHideMembers(!hideMembers)}>
        <KTooltip
          position="bottom"
          children={<img src={hideMembers ? "/account_icons/eye-closed.svg" : "/account_icons/eye-open.svg"} />}
          content={<KSpan text={hideMembers ? "Show Members" : "Hide Members"} />}
        />
      </div>
      <div
        className="flex items-center py-[13px] pl-3.5 pr-3 gap-2 rounded-[10px] w-[300px]"
        style={{
          background: searchValue ? "#FFF" : "#F7F7F7",
          border: "1px solid #F3F3F3",
          height: "48px",
          transition: "0.3s"
        }}
      >
        <KInput
          dataTestId="search-input"
          value={searchValue}
          padding="0px"
          background="transparent"
          onChange={(text: string) => {
            setSearchValue(text)
            setPageNumber((prev) => (prev === 1 ? prev : 1)) // Always 1
          }}
          leftIcon="/catalog_icons/search-grey.svg"
          gap="8px"
          borderRadius={0}
          shadowDisabled
          placeholder={"Search..."}
        />
        <KDropdown
          rightIcon="/admin_icons/caret-down.svg"
          options={searchOptions}
          selected={selectedSearchOption}
          onSelect={(selected) => setSelectedSearchOption(selected as KSelectOption)}
          padding="0px"
          background="transparent"
          activeBackground="transparent"
          shadowDisabled={true}
          enableIndicator={true}
          menuWidth={140}
          width={140}
          menuLeftMargin={-40}
          textColor="#F3F3F3"
        />
      </div>
      {checkAdminPermission(ADMIN_PERMISSIONS.USER_ASSET_WRITE, props.loggedInUser?.adminPermissions) && (
        <div className="flex">
          <KButton
            leftIcon={PlusIcon}
            background="#000"
            hoverBackground="#111"
            activeBackground="#000"
            onClick={() => onClickAdd()}
            width="auto"
            height="48px"
            padding="14px"
            text="New User"
            textColor="#FFF"
            gap="4px"
          />
        </div>
      )}

      {checkAdminPermission(ADMIN_PERMISSIONS.USER_ASSET_WRITE, props.loggedInUser?.adminPermissions) && (
        <div className="flex">
          <KButton
            leftIcon={PlusIcon}
            background="#000"
            hoverBackground="#111"
            activeBackground="#000"
            onClick={() => setNewLabelModal(true)}
            width="auto"
            height="48px"
            padding="14px"
            text="New Label"
            textColor="#FFF"
            gap="4px"
          />
        </div>
      )}
      {checkAdminPermission(ADMIN_PERMISSIONS.USER_ASSET_RELOAD_REPORT, props.loggedInUser?.adminPermissions) && (
        <div className="flex">
          <KButton
            text="Reload Reports"
            height="48px"
            width="140px"
            background="#F7F7F7"
            hoverBackground="#F3F3F3"
            padding="14px"
            onClick={() => setReloadModalOpen(true)}
          />
        </div>
      )}
    </div>
  )

  const tableTitle = (title: string) => (
    <div className="flex flex-row gap-2 items-center !cursor-default">
      <KTooltip
        backgroundColor="#000"
        padding="0px 4px"
        height="20px"
        arrowColor="#000"
        position="bottom"
        content={
          <div className="h-5 w-max flex items-center">
            <KSpan text={openSidebar ? "Hide sidebar" : "Open sidebar"} fontSize={12} lineHeight="16px" color="#FFF" />
          </div>
        }
      >
        <div className="flex items-center">
          <KButton
            icon="/left-sidebar.svg"
            width="32px"
            height="32px"
            background="transparent"
            shadowDisabled={true}
            padding="6px"
            onClick={() => {
              if (setOpenSidebar) {
                setOpenSidebar(!openSidebar)
              }
            }}
          />
        </div>
      </KTooltip>
      <div>
        <KTitleSpan text={title} color="#111" fontSize={20} lineHeight="28px" />
      </div>
    </div>
  )

  const subHeaderElements = () => {
    if (!filterOpen) {
      return undefined
    }
    return <AdminUserFilter filter={advanceFilter} setFilter={setAdvanceFilter} onApplyFilter={onApplyAdvanceFilter} />
  }

  const renderLeftSidebar = () => {
    return (
      <div
        className={`${
          openSidebar ? "min-w-[230px]" : "min-w-0 !w-[0px]"
        } max-w-[320px] w-[20%] bg-[#F7F7F7] min-h-[100vh] flex flex-col overflow-hidden shrink-0`}
        style={{
          transitionDuration: "0.5s"
        }}
      >
        <div className="pl-6 pr-3 py-4 whitespace-nowrap">
          <KTitleSpan text={"User Asset"} lineHeight="28px" fontSize={20} color="#111" />
        </div>
        <div className="py-1.5 px-4 h-11">
          <div
            className={`rounded-[10px] p-2 flex flex-row items-center gap-2 ${
              selectedPage === PageOptions.USERS ? "bg-[#FFF]" : "transparent"
            } cursor-pointer whitespace-nowrap`}
            onClick={() => {
              setSelectedPage(PageOptions.USERS)
              setSelectedUserTypes([])
              setPageNumber(1)
              setUserRolesOpen(!userRolesOpen)
            }}
          >
            <img src={"/reports_icons/user-circle.svg"} width={16} height={16} className="shrink-0" />
            <KSpan text={"Users"} fontSize={12} lineHeight="16px" fontWeight={500} color="#111" />
          </div>
        </div>
        {selectedPage === PageOptions.USERS &&
          userRolesOpen &&
          [...userTypeOptions].map((option, index: number) => {
            const selected =
              option.value === -1 ? selectedUserTypes.length === 0 : selectedUserTypes.includes(option.value)
            return (
              <div className="py-1.5 px-12 h-11" key={`${index}`}>
                <div
                  className={`rounded-[10px] p-2 flex flex-row items-center gap-2 ${
                    selected ? "bg-[#FFF]" : "transparent"
                  } cursor-pointer whitespace-nowrap`}
                  onClick={() => {
                    if (option.value === -1) {
                      setSelectedUserTypes([])
                    } else if (selectedUserTypes.includes(option.value)) {
                      const filteredUserTypes = [...selectedUserTypes].filter(
                        (userTypeRoleId: number) => userTypeRoleId !== option.value
                      )
                      setSelectedUserTypes(filteredUserTypes)
                    } else {
                      setSelectedUserTypes([...selectedUserTypes, option.value])
                    }
                    setPageNumber(1)
                  }}
                >
                  <KSpan text={`- ${option.label}`} fontSize={12} lineHeight="16px" fontWeight={500} color="#111" />
                </div>
              </div>
            )
          })}
        {(props.userHasUpdateCount ?? 0) > 0 && (
          <div className="py-1.5 px-4">
            <div
              className="rounded-[10px] p-2 flex flex-row items-center gap-2 cursor-pointer whitespace-wrap transition-colors duration-200 ease-in-out hover:bg-[#FFE0B2]"
              style={{
                border: "1px solid #EF4444",
                backgroundColor: "#FDECEC",
                boxShadow: additionalFilters.has_update
                  ? "inset 0 2px 6px 0 rgba(239, 68, 68, 0.50) , inset 0 -2px 6px 0 rgba(239, 68, 68, 0.50)"
                  : "",
                transform: additionalFilters.has_update ? "scale(0.97)" : ""
              }}
              onClick={() => {
                setAdditionalFilters((prev) => ({
                  ...prev,
                  has_update: prev.has_update === "true" ? "" : "true",
                  contract_verified: "",
                  payment_method_updated: ""
                }))
                setPageNumber(1)
              }}
            >
              <span style={{ fontSize: 12, lineHeight: "16px", fontWeight: 500, color: "#B91C1C" }}>
                Warning: There are <b>{props.userHasUpdateCount}</b> deal updates!
              </span>
            </div>
          </div>
        )}
        {(props.userUnverifiedContractCount ?? 0) > 0 && (
          <div className="py-1.5 px-4">
            <div
              className="rounded-[10px] p-2 flex flex-row items-center gap-2 cursor-pointer whitespace-wrap transition-colors duration-200 ease-in-out hover:bg-[#FFE0B2]"
              style={{
                border: "1px solid #FFB74D",
                backgroundColor: "#FFF4E5",
                boxShadow: additionalFilters.contract_verified
                  ? "inset 0 2px 6px 0 rgba(255, 183, 77, 0.50) , inset 0 -2px 6px 0 rgba(255, 183, 77, 0.50)"
                  : "",
                transform: additionalFilters.contract_verified ? "scale(0.97)" : ""
              }}
              onClick={() => {
                setAdditionalFilters((prev) => ({
                  ...prev,
                  contract_verified: prev.contract_verified === "false" ? "" : "false",
                  has_update: "",
                  payment_method_updated: ""
                }))
                setPageNumber(1)
              }}
            >
              <span style={{ fontSize: 12, lineHeight: "16px", fontWeight: 500, color: "#B26A00" }}>
                Warning: There are <b>{props.userUnverifiedContractCount}</b> users with unverified contracts!
              </span>
            </div>
          </div>
        )}
        {(props.userPaymentMethodUpdatedCount ?? 0) > 0 && (
          <div className="py-1.5 px-4">
            <div
              className="rounded-[10px] p-2 flex flex-row items-center gap-2 cursor-pointer whitespace-wrap transition-colors duration-200 ease-in-out hover:bg-[#D6F2E1]"
              style={{
                border: "1px solid #22C55E",
                backgroundColor: "#ECFAF1",
                boxShadow: additionalFilters.payment_method_updated
                  ? "inset 0 2px 6px 0 rgba(34, 197, 94, 0.50) , inset 0 -2px 6px 0 rgba(34, 197, 94, 0.50)"
                  : "",
                transform: additionalFilters.payment_method_updated ? "scale(0.97)" : ""
              }}
              onClick={() => {
                setAdditionalFilters((prev) => ({
                  ...prev,
                  payment_method_updated: prev.payment_method_updated === "true" ? "" : "true",
                  has_update: "",
                  contract_verified: ""
                }))
                setPageNumber(1)
              }}
            >
              <span style={{ fontSize: 12, lineHeight: "16px", fontWeight: 500, color: "#15803D" }}>
                Warning: There are <b>{props.userPaymentMethodUpdatedCount}</b> users who changed their payment details!
              </span>
            </div>
          </div>
        )}
        {canWorkLabelRequests && openLabelRequestCount > 0 && (
          <div className="py-1.5 px-4">
            {/* With the warnings at the top, so it is seen as soon as the page opens. Unlike them it is not
                a filter on the user list: the work is a list of requests, so it opens that screen. */}
            <div
              className="rounded-[10px] p-2 flex flex-row items-center gap-2 cursor-pointer whitespace-wrap transition-colors duration-200 ease-in-out hover:bg-[#D7E9FB]"
              style={{ border: "1px solid #3B82F6", backgroundColor: "#E8F1FD" }}
              onClick={() => {
                setSelectedPage(PageOptions.LABEL_REQUESTS)
                setSelectedUserTypes([])
                setPageNumber(1)
              }}
            >
              <KSpan
                text={`Warning: There are ${openLabelRequestCount} label requests!`}
                fontSize={12}
                lineHeight="16px"
                fontWeight={500}
                color="#1D4ED8"
              />
            </div>
          </div>
        )}
        <div className="py-1.5 px-4 mt-4 pt-6" style={{ borderTop: "1px dashed grey" }}>
          <div
            className={`rounded-[10px] p-2 flex flex-row items-center gap-2 ${
              selectedPage === PageOptions.USER_LOGS ? "bg-[#FFF]" : "transparent"
            } cursor-pointer whitespace-nowrap`}
            onClick={() => {
              setSelectedPage(PageOptions.USER_LOGS)
              setSelectedUserTypes([])
              setPageNumber(1)
            }}
          >
            <img src={"/catalog_icons/history.svg"} width={16} height={16} className="shrink-0" />
            <KSpan text={"User Logs"} fontSize={12} lineHeight="16px" fontWeight={500} color="#111" />
          </div>
        </div>
        <div className="py-1.5 px-4 h-11">
          <div
            className={`rounded-[10px] p-2 flex flex-row items-center gap-2 ${
              selectedPage === PageOptions.TAKEDOWN_LOGS ? "bg-[#FFF]" : "transparent"
            } cursor-pointer whitespace-nowrap`}
            onClick={() => {
              setSelectedPage(PageOptions.TAKEDOWN_LOGS)
              setSelectedUserTypes([])
              setPageNumber(1)
            }}
          >
            <img src={"/catalog_icons/history.svg"} width={16} height={16} className="shrink-0" />
            <KSpan text={"Takedown Logs"} fontSize={12} lineHeight="16px" fontWeight={500} color="#111" />
          </div>
        </div>
        <div className="py-1.5 px-4 h-11">
          <div
            className={`rounded-[10px] p-2 flex flex-row items-center gap-2 ${
              selectedPage === PageOptions.DRM_LOGS ? "bg-[#FFF]" : "transparent"
            } cursor-pointer whitespace-nowrap`}
            onClick={() => {
              setSelectedPage(PageOptions.DRM_LOGS)
              setSelectedUserTypes([])
              setPageNumber(1)
            }}
          >
            <img src={"/catalog_icons/history.svg"} width={16} height={16} className="shrink-0" />
            <KSpan text={"DRM Logs"} fontSize={12} lineHeight="16px" fontWeight={500} color="#111" />
          </div>
        </div>
        <div className="py-1.5 px-4 h-11">
          <div
            className={`rounded-[10px] p-2 flex flex-row items-center gap-2 ${
              selectedPage === PageOptions.EXCLUSIVITY_LOGS ? "bg-[#FFF]" : "transparent"
            } cursor-pointer whitespace-nowrap`}
            onClick={() => {
              setSelectedPage(PageOptions.EXCLUSIVITY_LOGS)
              setSelectedUserTypes([])
              setPageNumber(1)
            }}
          >
            <img src={"/catalog_icons/history.svg"} width={16} height={16} className="shrink-0" />
            <KSpan text={"Exclusivity Logs"} fontSize={12} lineHeight="16px" fontWeight={500} color="#111" />
          </div>
        </div>
        {/* Straight under Takedown Logs, because that is the log a claim hangs off: the co-manager runs
            the takedown check for the one release the artist picked, and the claim owns that check. */}
        <div className="py-1.5 px-4 h-11">
          <div
            className={`rounded-[10px] p-2 flex flex-row items-center gap-2 ${
              selectedPage === PageOptions.CLAIM_LOGS ? "bg-[#FFF]" : "transparent"
            } cursor-pointer whitespace-nowrap`}
            onClick={() => {
              setSelectedPage(PageOptions.CLAIM_LOGS)
              setSelectedUserTypes([])
              setPageNumber(1)
            }}
          >
            <img src={"/catalog_icons/history.svg"} width={16} height={16} className="shrink-0" />
            <KSpan text={"Claim DRM Logs"} fontSize={12} lineHeight="16px" fontWeight={500} color="#111" />
          </div>
        </div>
        {canWorkLabelRequests && (
          <div className="py-1.5 px-4 h-11">
            <div
              className={`rounded-[10px] p-2 flex flex-row items-center gap-2 ${
                selectedPage === PageOptions.LABEL_REQUESTS ? "bg-[#FFF]" : "transparent"
              } cursor-pointer whitespace-nowrap`}
              onClick={() => {
                setSelectedPage(PageOptions.LABEL_REQUESTS)
                setSelectedUserTypes([])
                setPageNumber(1)
              }}
            >
              <img src={"/catalog_icons/history.svg"} width={16} height={16} className="shrink-0" />
              <KSpan text={"Label Requests"} fontSize={12} lineHeight="16px" fontWeight={500} color="#111" />
            </div>
          </div>
        )}
        <div className="py-1.5 px-4 h-11">
          <div
            className={`rounded-[10px] p-2 flex flex-row items-center gap-2 ${
              selectedPage === PageOptions.NOTIFICATIONS ? "bg-[#FFF]" : "transparent"
            } cursor-pointer whitespace-nowrap`}
            onClick={() => {
              setSelectedPage(PageOptions.NOTIFICATIONS)
              setSelectedUserTypes([])
              setPageNumber(1)
            }}
          >
            <img src={"/catalog_icons/history.svg"} width={16} height={16} className="shrink-0" />
            <KSpan text={"Notification Logs"} fontSize={12} lineHeight="16px" fontWeight={500} color="#111" />
          </div>
        </div>
        <div className="py-1.5 px-4 h-11">
          <div
            className={`rounded-[10px] p-2 flex flex-row items-center gap-2 ${
              selectedPage === PageOptions.ANNOUNCEMENTS ? "bg-[#FFF]" : "transparent"
            } cursor-pointer whitespace-nowrap`}
            onClick={() => {
              setSelectedPage(PageOptions.ANNOUNCEMENTS)
              setSelectedUserTypes([])
              setPageNumber(1)
            }}
          >
            <img src={"/catalog_icons/history.svg"} width={16} height={16} className="shrink-0" />
            <KSpan text={"Announcement Logs"} fontSize={12} lineHeight="16px" fontWeight={500} color="#111" />
          </div>
        </div>
        {/* Its own permission, unlike the logs above: the conversations carry the artist's balance and payouts. */}
        {checkAdminPermission(ADMIN_PERMISSIONS.LOGS_CO_MANAGER_LOGS, props.loggedInUser?.adminPermissions) && (
          <div className="py-1.5 px-4 h-11">
            <div
              className={`rounded-[10px] p-2 flex flex-row items-center gap-2 ${
                selectedPage === PageOptions.CO_MANAGER_LOGS ? "bg-[#FFF]" : "transparent"
              } cursor-pointer whitespace-nowrap`}
              onClick={() => {
                setSelectedPage(PageOptions.CO_MANAGER_LOGS)
                setSelectedUserTypes([])
                setPageNumber(1)
              }}
            >
              <img src={"/catalog_icons/history.svg"} width={16} height={16} className="shrink-0" />
              <KSpan text={"Co-Manager Logs"} fontSize={12} lineHeight="16px" fontWeight={500} color="#111" />
            </div>
          </div>
        )}
        {openClaimCount > 0 && (
          <div className="py-1.5 px-4">
            <div
              className="rounded-[10px] p-2 flex flex-row items-center gap-2 cursor-pointer whitespace-wrap transition-colors duration-200 ease-in-out hover:bg-[#D7E9FB]"
              style={{
                border: "1px solid #3B82F6",
                backgroundColor: "#E8F1FD"
              }}
              /* Unlike the warning strips at the top, this one is not a filter on the user list: the
                 work is a queue of reports rather than a set of accounts, so it opens a screen, and it
                 sits with the log list it belongs to rather than with them. That is also why it carries
                 neither the shrink nor the inset shadow they show while selected: those read as a filter
                 being held down, but this one stays selected for as long as its screen is open, so both
                 effects were on permanently - the scale left it narrower than the strips and the shadow
                 left it looking pressed. */
              onClick={() => {
                setSelectedPage(PageOptions.CLAIM_LOGS)
                setSelectedUserTypes([])
                setPageNumber(1)
              }}
            >
              <span style={{ fontSize: 12, lineHeight: "16px", fontWeight: 500, color: "#1D4ED8" }}>
                Warning: There are <b>{openClaimCount}</b> claim DRM logs!
              </span>
            </div>
          </div>
        )}
        <div className="py-1.5 px-4 mt-4 pt-6" style={{ borderTop: "1px dashed grey" }}>
          <div
            className={`rounded-[10px] p-2 flex flex-row items-center gap-2 cursor-pointer whitespace-nowrap`}
            onClick={() => {
              navigator.clipboard.writeText(`${process.env.REACT_APP_FRONT_END_URL}/?release_submission=1`)
              toast.success("Release Submission Link successfully copied")
            }}
          >
            <img src="/reports_icons/copy-clipboard.svg" width={18} />
            <KSpan text={"Release Submission Link"} fontSize={12} lineHeight="16px" fontWeight={500} color="#111" />
          </div>
        </div>
        <div className="px-4">
          <div
            className={`rounded-[10px] p-2 flex flex-row items-center gap-2 cursor-pointer whitespace-nowrap`}
            onClick={() => {
              navigator.clipboard.writeText(`${process.env.REACT_APP_FRONT_END_URL}/?takedown_overview=1`)
              toast.success("Advance Takedown Catalog Link successfully copied")
            }}
          >
            <img src="/reports_icons/copy-clipboard.svg" width={18} />
            <KSpan text={"Advance & Takedown Link"} fontSize={12} lineHeight="16px" fontWeight={500} color="#111" />
          </div>
        </div>
      </div>
    )
  }

  const renderUserView = () => {
    return (
      <div className="grow flex justify-center overflow-x-auto">
        <div className="w-full">
          <TableNew
            title={tableTitle("User Asset")}
            pagination
            limit={adminTableLimit}
            columns={columns}
            minRowCount={adminTableLimit}
            data={users}
            count={props.paginatedUsers.count}
            onClickPage={onClickPage}
            topRightElement={topRightElements}
            subHeaderElements={subHeaderElements}
            onSelectRow={
              checkAdminPermission(ADMIN_PERMISSIONS.USER_ASSET_WRITE, props.loggedInUser?.adminPermissions)
                ? (index, row) => onSelectRow(index)
                : undefined
            }
            loading={loading}
            onSortOrFilterChange={onSortOrFilterChange}
            pageNumber={pageNumber}
            additionalFilters={additionalFilters}
            onMouseEnterRow={(row: any, index: number) => {
              setHoveredRowId(index)
            }}
            onMouseLeaveRow={(row: any, index: number) => {
              if (hoveredRowId === index) {
                setHoveredRowId(undefined)
              }
            }}
            hoveredRowId={hoveredRowId}
            overrideTableOverflow="auto"
          />
          <ReloadModal
            isOpen={reloadModalOpen}
            handleClose={() => {
              setReloadModalOpen(false)
            }}
          />
          <AdminNewLabelModal open={newLabelModal} onClose={() => setNewLabelModal(false)} />
          {props.selectedUser && (
            <AdminUserCreateModal
              open={props.selectedUser !== null && addEditUserModal}
              onClose={() => {
                setAddEditUserModalOpen(false)
              }}
              isEdit={props.selectedUser?.email !== ""}
            />
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="catalog flex bg-[#fff]">
      {renderLeftSidebar()}
      {selectedPage === PageOptions.USERS && renderUserView()}
      {selectedPage === PageOptions.USER_LOGS && (
        <AdminUserLog params={props.params} setParams={props.setParams} headerDiv={tableTitle("User Logs")} />
      )}
      {selectedPage === PageOptions.TAKEDOWN_LOGS && (
        <AdminTakedownLog params={props.params} setParams={props.setParams} headerDiv={tableTitle("Takedown Logs")} />
      )}
      {selectedPage === PageOptions.DRM_LOGS && (
        <AdminDrmLog params={props.params} setParams={props.setParams} headerDiv={tableTitle("DRM Logs")} />
      )}
      {selectedPage === PageOptions.CLAIM_LOGS && (
        <AdminClaimLog params={props.params} setParams={props.setParams} headerDiv={tableTitle("Claim DRM Logs")} />
      )}
      {selectedPage === PageOptions.LABEL_REQUESTS && (
        <AdminLabelRequest headerDiv={tableTitle("Label Requests")} onChange={fetchOpenLabelRequestCount} />
      )}
      {selectedPage === PageOptions.EXCLUSIVITY_LOGS && (
        <AdminExclusivityLog
          params={props.params}
          setParams={props.setParams}
          headerDiv={tableTitle("Exclusivity Logs")}
        />
      )}
      {selectedPage === PageOptions.NOTIFICATIONS && (
        <AdminNotificationWatch
          params={props.params}
          setParams={props.setParams}
          headerDiv={tableTitle("Notification Logs")}
        />
      )}
      {selectedPage === PageOptions.ANNOUNCEMENTS && (
        <AdminAnnouncementWatch
          params={props.params}
          setParams={props.setParams}
          headerDiv={tableTitle("Announcement Logs")}
        />
      )}
      {selectedPage === PageOptions.CO_MANAGER_LOGS && (
        <AdminCoManagerLog params={props.params} setParams={props.setParams} headerDiv={tableTitle("Co-Manager Logs")} />
      )}
    </div>
  )
}

const mapStateToProps = (state: RootState) => {
  return {
    paginatedUsers: state.admin.paginatedUsers,
    selectedUser: state.admin.selectedUser,
    lastUpdate: state.admin.lastUpdate,
    loggedInUser: state.auth.user,
    userHasUpdateCount: state.admin.userHasUpdateCount,
    userUnverifiedContractCount: state.admin.userUnverifiedContractCount,
    userPaymentMethodUpdatedCount: state.admin.userPaymentMethodUpdatedCount
  }
}

export default connect(mapStateToProps, {
  fetchAllUsers,
  selectUser,
  fetchLastUpdate,
  refreshLabelCamp,
  refreshReports,
  refreshSummaries
})(AdminUser)
